import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import GiftCard from "@/components/GiftCard";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Heart, Trash2, RotateCw, Clock, LogIn, Loader2, CalendarClock, Mail, CalendarPlus } from "lucide-react";

const cap = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s);

export default function Saved() {
  const navigate = useNavigate();
  const { user, loading, setAuthOpen } = useAuth();
  const [recipients, setRecipients] = useState([]);
  const [searches, setSearches] = useState([]);
  const [wishlist, setWishlist] = useState([]);
  const [upcoming, setUpcoming] = useState([]);
  const [busyId, setBusyId] = useState(null);
  const [sendingReminders, setSendingReminders] = useState(false);

  const [dateOpen, setDateOpen] = useState(false);
  const [dateTarget, setDateTarget] = useState(null);
  const [dateVal, setDateVal] = useState("");
  const [reminderVal, setReminderVal] = useState("7");

  const loadAll = () => {
    api.get("/recipients").then((r) => setRecipients(r.data)).catch(() => {});
    api.get("/searches").then((r) => setSearches(r.data)).catch(() => {});
    api.get("/wishlist").then((r) => setWishlist(r.data)).catch(() => {});
    api.get("/reminders/upcoming").then((r) => setUpcoming(r.data)).catch(() => {});
  };

  useEffect(() => { if (user) loadAll(); }, [user]);

  const rerun = async (answers, id) => {
    setBusyId(id);
    try {
      const res = await api.post("/recommend", { answers });
      sessionStorage.setItem("gm_result", JSON.stringify({ ...res.data, answers }));
      navigate("/results");
    } catch { toast.error("Could not re-run search"); }
    finally { setBusyId(null); }
  };

  const remove = async (id) => {
    await api.delete(`/recipients/${id}`).catch(() => {});
    setRecipients((p) => p.filter((r) => r.recipient_id !== id));
    loadAll();
    toast.success("Removed");
  };

  const openDate = (r) => {
    setDateTarget(r);
    setDateVal(r.occasion_date || "");
    setReminderVal(String(r.reminder_days || 7));
    setDateOpen(true);
  };

  const saveDate = async () => {
    try {
      await api.put(`/recipients/${dateTarget.recipient_id}`, {
        occasion_date: dateVal || null, reminder_days: parseInt(reminderVal) || 7,
      });
      toast.success("Occasion date saved");
      setDateOpen(false);
      loadAll();
    } catch { toast.error("Could not save date"); }
  };

  const sendReminders = async () => {
    setSendingReminders(true);
    try {
      const res = await api.post("/reminders/run");
      if (res.data.sent > 0) toast.success(`Sent ${res.data.sent} reminder email${res.data.sent > 1 ? "s" : ""} to ${user.email}`);
      else toast(res.data.message || "No reminders due right now");
      loadAll();
    } catch { toast.error("Could not send reminders"); }
    finally { setSendingReminders(false); }
  };

  if (loading) {
    return <div className="min-h-[60vh] flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin text-brand-green" /></div>;
  }

  if (!user) {
    return (
      <div className="max-w-md mx-auto px-4 py-24 text-center gm-fade">
        <span className="w-14 h-14 rounded-full bg-brand-green/10 flex items-center justify-center mx-auto mb-5">
          <Heart className="w-7 h-7 text-brand-green" />
        </span>
        <h1 className="font-serif text-3xl mb-3">Sign in to see your saved gifts</h1>
        <p className="text-muted-foreground mb-7">Save recipients, keep a wishlist, and get email reminders before the big date. The quiz itself never needs an account.</p>
        <Button onClick={() => setAuthOpen(true)} data-testid="saved-signin-button" className="bg-brand-green hover:bg-brand-greendark rounded-full gap-2">
          <LogIn className="w-4 h-4" /> Sign in
        </Button>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 lg:px-10 py-8 lg:py-12 gm-fade">
      <h1 className="font-serif text-3xl lg:text-5xl mb-2">Saved & history</h1>
      <p className="text-muted-foreground mb-10">Welcome back, {user.name?.split(" ")[0]} — your wishlist, recipients and reminders.</p>

      {/* Upcoming occasions */}
      <section className="mb-14">
        <div className="flex items-center justify-between flex-wrap gap-3 mb-5">
          <h2 className="font-serif text-2xl flex items-center gap-2"><CalendarClock className="w-5 h-5 text-brand-amber" /> Upcoming occasions</h2>
          {upcoming.length > 0 && (
            <Button onClick={sendReminders} disabled={sendingReminders} data-testid="send-reminders-button"
              className="bg-brand-green hover:bg-brand-greendark rounded-full gap-2">
              {sendingReminders ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mail className="w-4 h-4" />} Email me my reminders
            </Button>
          )}
        </div>
        {upcoming.length === 0 ? (
          <div className="bg-white rounded-2xl border border-brand-cream2 p-8 text-muted-foreground">
            No dates set yet. Add an occasion date to a saved recipient to get an email reminder.
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {upcoming.map((u) => (
              <div key={u.recipient_id} data-testid={`upcoming-${u.recipient_id}`}
                className="bg-white rounded-2xl border border-brand-cream2 p-5 flex items-center gap-4">
                <div className="w-14 h-14 rounded-xl bg-brand-green/10 flex flex-col items-center justify-center shrink-0">
                  <span className="font-mono font-bold text-brand-green text-lg leading-none">{u.days_until}</span>
                  <span className="text-[10px] text-muted-foreground">days</span>
                </div>
                <div className="min-w-0">
                  <p className="font-serif text-lg truncate">{u.name}</p>
                  <p className="text-xs text-muted-foreground capitalize">
                    {u.occasion?.replace(/_/g, " ")} · {new Date(u.occasion_date).toLocaleDateString("en-GB", { day: "numeric", month: "short" })}
                  </p>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* Wishlist */}
      <section className="mb-14">
        <h2 className="font-serif text-2xl mb-5 flex items-center gap-2"><Heart className="w-5 h-5 text-brand-amber" /> Your wishlist</h2>
        {wishlist.length === 0 ? (
          <div className="bg-white rounded-2xl border border-brand-cream2 p-8 text-muted-foreground">
            No hearted gifts yet. Tap the <Heart className="w-3.5 h-3.5 inline mx-0.5" /> on any gift while browsing to save it here.
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {wishlist.map((p, i) => (
              <GiftCard key={p.product_id} rec={{ product: p, label: cap(p.category), match: null }}
                index={i} sessionId="wishlist" showWhy={false} />
            ))}
          </div>
        )}
      </section>

      {/* Recipients */}
      <section className="mb-14">
        <h2 className="font-serif text-2xl mb-5 flex items-center gap-2"><CalendarPlus className="w-5 h-5 text-brand-amber" /> Saved recipients</h2>
        {recipients.length === 0 ? (
          <div className="bg-white rounded-2xl border border-brand-cream2 p-8 text-muted-foreground">
            No saved recipients yet. Complete a search and tap <b>Save recipient</b>.
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {recipients.map((r) => (
              <div key={r.recipient_id} data-testid={`recipient-card-${r.recipient_id}`}
                className="bg-white rounded-2xl border border-brand-cream2 p-6 hover:shadow-lg transition-shadow flex flex-col">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="font-serif text-xl">{r.name}</p>
                    <p className="text-xs text-muted-foreground mt-1 capitalize">
                      {r.answers?.occasion?.replace(/_/g, " ")} · {r.answers?.budget_label}
                    </p>
                  </div>
                  <button onClick={() => remove(r.recipient_id)} data-testid={`recipient-delete-${r.recipient_id}`}
                    className="text-muted-foreground hover:text-brand-burgundy transition-colors">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
                {r.answers?.about && <p className="text-sm text-muted-foreground mt-3 line-clamp-2">"{r.answers.about}"</p>}
                <button onClick={() => openDate(r)} data-testid={`recipient-setdate-${r.recipient_id}`}
                  className="mt-3 inline-flex items-center gap-1.5 text-xs font-medium text-brand-green hover:text-brand-amber transition-colors">
                  <CalendarClock className="w-3.5 h-3.5" />
                  {r.occasion_date ? `Reminder set: ${new Date(r.occasion_date).toLocaleDateString("en-GB")}` : "Set occasion date"}
                </button>
                <Button onClick={() => rerun(r.answers, r.recipient_id)} disabled={busyId === r.recipient_id}
                  data-testid={`recipient-rerun-${r.recipient_id}`}
                  className="mt-4 w-full bg-brand-green hover:bg-brand-greendark rounded-full gap-2">
                  {busyId === r.recipient_id ? <Loader2 className="w-4 h-4 animate-spin" /> : <RotateCw className="w-4 h-4" />}
                  Re-run search
                </Button>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* Recent searches */}
      <section>
        <h2 className="font-serif text-2xl mb-5 flex items-center gap-2"><Clock className="w-5 h-5 text-brand-amber" /> Recent searches</h2>
        {searches.length === 0 ? (
          <div className="bg-white rounded-2xl border border-brand-cream2 p-8 text-muted-foreground">No searches yet.</div>
        ) : (
          <div className="bg-white rounded-2xl border border-brand-cream2 divide-y divide-brand-cream2">
            {searches.map((s) => (
              <div key={s.search_id} className="flex items-center justify-between p-5 gap-4">
                <div>
                  <p className="font-medium capitalize">{cap(s.answers?.recipient)} · {s.answers?.occasion?.replace(/_/g, " ")}</p>
                  <p className="text-xs text-muted-foreground mt-0.5">{s.answers?.budget_label} · {s.count} gifts · {new Date(s.created_at).toLocaleDateString("en-GB")}</p>
                </div>
                <Button variant="outline" size="sm" onClick={() => rerun(s.answers, s.search_id)} disabled={busyId === s.search_id}
                  data-testid={`search-rerun-${s.search_id}`} className="rounded-full border-brand-green/30 hover:bg-brand-cream2 gap-2 shrink-0">
                  {busyId === s.search_id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RotateCw className="w-3.5 h-3.5" />} Re-run
                </Button>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* Set date dialog */}
      <Dialog open={dateOpen} onOpenChange={setDateOpen}>
        <DialogContent className="bg-white" data-testid="setdate-dialog">
          <DialogHeader><DialogTitle className="font-serif text-2xl">Occasion date</DialogTitle></DialogHeader>
          <div className="py-2 space-y-4">
            <div>
              <Label htmlFor="occ-date">Date</Label>
              <Input id="occ-date" type="date" data-testid="setdate-input" value={dateVal} onChange={(e) => setDateVal(e.target.value)} />
            </div>
            <div>
              <Label htmlFor="occ-remind">Remind me</Label>
              <select id="occ-remind" data-testid="setdate-reminder-select" value={reminderVal}
                onChange={(e) => setReminderVal(e.target.value)}
                className="w-full h-10 rounded-md border border-input bg-white px-3 text-sm">
                <option value="3">3 days before</option>
                <option value="7">1 week before</option>
                <option value="14">2 weeks before</option>
                <option value="30">1 month before</option>
              </select>
            </div>
            <p className="text-xs text-muted-foreground">We'll email {user.email} a gentle reminder.</p>
          </div>
          <DialogFooter>
            <Button onClick={saveDate} data-testid="setdate-save-button" className="bg-brand-green hover:bg-brand-greendark rounded-full">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
